import { FC } from "react";
import { Navigate, Outlet } from "react-router-dom";

import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { Roles } from "src/core/constants/roles";
import { useUser } from "src/core/contexts/UserContext";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { usePermissions } from "src/core/hooks/usePermissions";

type RoleGuardProps = {
  allowedRoles: Roles[];
  /** Destination si le rôle n'est pas autorisé. Défaut : page 403 du tenant. */
  unauthorizedRedirect?: "home";
};

/**
 * RoleGuard :
 * Restreint l'accès aux routes enfants aux utilisateurs ayant au moins un des rôles autorisés.
 * Attend la fin du chargement utilisateur pour éviter un 403 prématuré (userGroups vide).
 */
export const RoleGuard: FC<RoleGuardProps> = ({ allowedRoles, unauthorizedRedirect }) => {
  const { loading } = useUser();
  const { hasAnyRole } = usePermissions();
  const paths = useRequiredTenantPaths();

  if (loading) {
    return <SpinnerFullPage />;
  }

  if (!hasAnyRole(allowedRoles)) {
    const to = unauthorizedRedirect === "home" ? paths.home() : paths.error403();
    return <Navigate to={to} replace />;
  }

  return <Outlet />;
};
